import {
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonNote,
  IonRefresher,
  IonRefresherContent,
  IonTitle,
  IonToggle,
  IonToolbar,
  type RefresherEventDetail,
  type ToggleChangeEventDetail,
  useIonViewWillEnter
} from "@ionic/react";
import {nativeAppAdapter} from "app/employee/nativeApp/FSAcademyAndroidAdapter";
import {useCallback, useState} from "react";
import type {IonRefresherCustomEvent, IonToggleCustomEvent} from "@ionic/core/dist/types/components";
import {useTranslation} from "react-i18next";
import {HomeBackButton} from "app/employee/menu/HomeBackButton";
import {MenuButton} from "app/employee/menu/MenuButton";
import {
  EmployeeNativeNotificationsStatusDocument,
  UpdateEmployeeNativeNotificationsEnabledDocument,
  UpdateFcmTokenDocument
} from "app/gql/graphqlSchema";
import {query} from "app/gql/client";
import {notificationsOutline} from "ionicons/icons";
import {useQuery} from "app/employee/controlCard/hook/useQuery";
import {isNil} from "app/utils/stdlib";

export const NativeNotificationSettings = () => {
  const {t} = useTranslation();

  const {data: hasNotificationsData, refetch, fetch} = useQuery(EmployeeNativeNotificationsStatusDocument);

  useIonViewWillEnter(() => {
    fetch({});
  }, [fetch]);

  const [deviceNotificationsEnabled, setDeviceNotificationsEnabled] = useState<boolean>(
    nativeAppAdapter.isNotificationPermissionGranted()
  );

  const [disabled, setDisabled] = useState<boolean>(false);

  const handleNotificationsChange = async (event: IonToggleCustomEvent<ToggleChangeEventDetail>) => {
    setDisabled(true);
    try {
      if (event.detail.checked) {
        const token = await nativeAppAdapter.requestNotificationToken();
        setDeviceNotificationsEnabled(nativeAppAdapter.isNotificationPermissionGranted());
        await query(UpdateFcmTokenDocument, {input: {token}});
        await query(UpdateEmployeeNativeNotificationsEnabledDocument, {input: {enabled: true}});
        console.log("Notification token received:", token);
      } else {
        await query(UpdateEmployeeNativeNotificationsEnabledDocument, {input: {enabled: false}});
      }
      await refetch();
    } finally {
      setDisabled(false);
    }
  };

  const settingDisabled = !nativeAppAdapter.isRunningInAndroidApp() || isNil(hasNotificationsData.value) || disabled;

  const notificationsEnabled =
    deviceNotificationsEnabled &&
    !isNil(hasNotificationsData.value) &&
    hasNotificationsData.value.getNativeNotificationsStatus.enabled &&
    hasNotificationsData.value.getNativeNotificationsStatus.hasFcmToken;

  const hint = (() => {
    if (nativeAppAdapter.isRunningInAndroidApp()) {
      if (!nativeAppAdapter.isNotificationPermissionGranted()) {
        return t("notifications.native.permission-hint");
      }
    }
    return null;
  })();

  const refreshStatus = useCallback(
    async (event: IonRefresherCustomEvent<RefresherEventDetail>) => {
      setDisabled(true);
      try {
        await refetch();
        setDeviceNotificationsEnabled(nativeAppAdapter.isNotificationPermissionGranted());
      } finally {
        setDisabled(false);
        event.detail.complete();
      }
    },
    [refetch]
  );
  return (
    <>
      <IonHeader>
        <IonToolbar>
          <HomeBackButton />
          <IonTitle>{t("notifications.native.title")}</IonTitle>
          <MenuButton />
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <IonRefresher onIonRefresh={refreshStatus} slot="fixed">
          <IonRefresherContent />
        </IonRefresher>
        <IonList>
          <IonItem disabled={settingDisabled} lines="full">
            <IonIcon icon={notificationsOutline} slot="start" />
            <IonToggle checked={notificationsEnabled} onIonChange={handleNotificationsChange}>
              <IonLabel>{t("notifications.native.enable-notifications")}</IonLabel>
              {!isNil(hint) && <IonNote className="ion-text-wrap">{hint}</IonNote>}
            </IonToggle>
          </IonItem>
        </IonList>
      </IonContent>
    </>
  );
};
